
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const ScientificCalculator = () => {
  const [display, setDisplay] = useState('0');
  const [result, setResult] = useState('');
  const [operation, setOperation] = useState('');
  const [firstOperand, setFirstOperand] = useState<number | null>(null);
  const [waitingForSecondOperand, setWaitingForSecondOperand] = useState(false);
  const [memory, setMemory] = useState<number>(0);

  const clearAll = () => {
    setDisplay('0');
    setResult('');
    setOperation('');
    setFirstOperand(null);
    setWaitingForSecondOperand(false);
  };

  const inputDigit = (digit: string) => {
    if (waitingForSecondOperand) {
      setDisplay(digit);
      setWaitingForSecondOperand(false);
    } else {
      setDisplay(display === '0' ? digit : display + digit);
    }
  };

  const inputDecimal = () => {
    if (waitingForSecondOperand) {
      setDisplay('0.');
      setWaitingForSecondOperand(false);
      return;
    }

    if (!display.includes('.')) {
      setDisplay(display + '.');
    }
  };

  const performOperation = (nextOperation: string) => {
    const inputValue = parseFloat(display);

    if (firstOperand === null) {
      setFirstOperand(inputValue);
    } else if (operation) {
      const currentResult = calculate();
      setResult(String(currentResult));
      setFirstOperand(currentResult);
    }

    setWaitingForSecondOperand(true);
    setOperation(nextOperation);
  };

  const calculate = () => {
    if (firstOperand === null || operation === '' || waitingForSecondOperand) {
      return parseFloat(display);
    }

    const secondOperand = parseFloat(display);
    let result = 0;

    switch (operation) {
      case '+':
        result = firstOperand + secondOperand;
        break;
      case '-':
        result = firstOperand - secondOperand;
        break;
      case '×':
        result = firstOperand * secondOperand;
        break;
      case '÷':
        result = firstOperand / secondOperand;
        break;
      case 'x^y':
        result = Math.pow(firstOperand, secondOperand);
        break;
      default:
        return secondOperand;
    }

    return result;
  };

  const handleEquals = () => {
    if (firstOperand === null && operation === '') {
      return;
    }

    const calculatedResult = calculate();
    setDisplay(String(calculatedResult));
    setResult('');
    setFirstOperand(calculatedResult);
    setOperation('');
    setWaitingForSecondOperand(true);
  };

  const handlePercentage = () => {
    const currentValue = parseFloat(display);
    const percentValue = currentValue / 100;
    setDisplay(String(percentValue));
  };

  const toggleSign = () => {
    const newValue = parseFloat(display) * -1;
    setDisplay(String(newValue));
  };

  const handleSquareRoot = () => {
    const currentValue = parseFloat(display);
    const sqrtValue = Math.sqrt(currentValue);
    setDisplay(String(sqrtValue));
  };

  const handleSquare = () => {
    const currentValue = parseFloat(display);
    const squareValue = currentValue * currentValue;
    setDisplay(String(squareValue));
  };

  const handleSin = () => {
    const currentValue = parseFloat(display);
    const sinValue = Math.sin(currentValue);
    setDisplay(String(sinValue));
  };

  const handleCos = () => {
    const currentValue = parseFloat(display);
    const cosValue = Math.cos(currentValue);
    setDisplay(String(cosValue));
  };

  const handleTan = () => {
    const currentValue = parseFloat(display);
    const tanValue = Math.tan(currentValue);
    setDisplay(String(tanValue));
  };

  const handleLog = () => {
    const currentValue = parseFloat(display);
    const logValue = Math.log10(currentValue);
    setDisplay(String(logValue));
  };

  const handleLn = () => {
    const currentValue = parseFloat(display);
    const lnValue = Math.log(currentValue);
    setDisplay(String(lnValue));
  };

  const handleMemoryAdd = () => {
    const currentValue = parseFloat(display);
    setMemory(memory + currentValue);
  };

  const handleMemorySubtract = () => {
    const currentValue = parseFloat(display);
    setMemory(memory - currentValue);
  };

  const handleMemoryRecall = () => {
    setDisplay(String(memory));
  };

  const handleMemoryClear = () => {
    setMemory(0);
  };

  return (
    <div className="w-full max-w-sm mx-auto">
      <div className="calculator-display min-h-[90px] mb-4">
        <div className="text-muted-foreground text-sm mb-1">
          {result && `${firstOperand} ${operation} ${waitingForSecondOperand ? '' : display}`}
        </div>
        <div className="text-2xl font-bold">{display}</div>
      </div>
      <Tabs defaultValue="functions" className="mb-4">
        <TabsList className="grid grid-cols-2">
          <TabsTrigger value="functions">Functions</TabsTrigger>
          <TabsTrigger value="memory">Memory</TabsTrigger>
        </TabsList>
        <TabsContent value="functions" className="grid grid-cols-4 gap-2">
          <Button variant="outline" className="calculator-button-operation" onClick={handleSin}>sin</Button>
          <Button variant="outline" className="calculator-button-operation" onClick={handleCos}>cos</Button>
          <Button variant="outline" className="calculator-button-operation" onClick={handleTan}>tan</Button>
          <Button variant="outline" className="calculator-button-operation" onClick={handleSquareRoot}>√</Button>
          
          <Button variant="outline" className="calculator-button-operation" onClick={handleLog}>log</Button>
          <Button variant="outline" className="calculator-button-operation" onClick={handleLn}>ln</Button>
          <Button variant="outline" className="calculator-button-operation" onClick={handleSquare}>x²</Button>
          <Button variant="outline" className="calculator-button-operation" onClick={() => performOperation('x^y')}>x^y</Button>
        </TabsContent>
        <TabsContent value="memory" className="grid grid-cols-4 gap-2">
          <Button variant="outline" className="calculator-button-operation" onClick={handleMemoryAdd}>M+</Button>
          <Button variant="outline" className="calculator-button-operation" onClick={handleMemorySubtract}>M-</Button>
          <Button variant="outline" className="calculator-button-operation" onClick={handleMemoryRecall}>MR</Button>
          <Button variant="outline" className="calculator-button-operation" onClick={handleMemoryClear}>MC</Button>
        </TabsContent>
      </Tabs>
      <div className="grid grid-cols-4 gap-2">
        <Button variant="outline" className="calculator-button-operation" onClick={clearAll}>
          C
        </Button>
        <Button variant="outline" className="calculator-button-operation" onClick={toggleSign}>
          +/-
        </Button>
        <Button variant="outline" className="calculator-button-operation" onClick={handlePercentage}>
          %
        </Button>
        <Button variant="outline" className="calculator-button-operation" onClick={() => performOperation('÷')}>
          ÷
        </Button>

        <Button variant="outline" className="calculator-button-number" onClick={() => inputDigit('7')}>
          7
        </Button>
        <Button variant="outline" className="calculator-button-number" onClick={() => inputDigit('8')}>
          8
        </Button>
        <Button variant="outline" className="calculator-button-number" onClick={() => inputDigit('9')}>
          9
        </Button>
        <Button variant="outline" className="calculator-button-operation" onClick={() => performOperation('×')}>
          ×
        </Button>

        <Button variant="outline" className="calculator-button-number" onClick={() => inputDigit('4')}>
          4
        </Button>
        <Button variant="outline" className="calculator-button-number" onClick={() => inputDigit('5')}>
          5
        </Button>
        <Button variant="outline" className="calculator-button-number" onClick={() => inputDigit('6')}>
          6
        </Button>
        <Button variant="outline" className="calculator-button-operation" onClick={() => performOperation('-')}>
          -
        </Button>

        <Button variant="outline" className="calculator-button-number" onClick={() => inputDigit('1')}>
          1
        </Button>
        <Button variant="outline" className="calculator-button-number" onClick={() => inputDigit('2')}>
          2
        </Button>
        <Button variant="outline" className="calculator-button-number" onClick={() => inputDigit('3')}>
          3
        </Button>
        <Button variant="outline" className="calculator-button-operation" onClick={() => performOperation('+')}>
          +
        </Button>

        <Button variant="outline" className="calculator-button-number col-span-2" onClick={() => inputDigit('0')}>
          0
        </Button>
        <Button variant="outline" className="calculator-button-number" onClick={inputDecimal}>
          .
        </Button>
        <Button variant="outline" className="calculator-button-operation" onClick={handleEquals}>
          =
        </Button>
      </div>
    </div>
  );
};

export default ScientificCalculator;
